import { pdf } from '@react-pdf/renderer';

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Button from 'components/field/Button';
import IntegerInput from 'components/field/IntegerInput';
import SwitchInput from 'components/field/SwitchInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Authorization from 'components/global/Authorization';
import Icon from 'components/global/Icon';
import Loading from 'components/global/Loading';
import Typography from 'components/global/Typography';

import useAccessControl from 'hooks/useAccessControl';
import useLocale from 'hooks/useLocale';
import useMediaQuery from 'hooks/useMediaQuery';

import MainTemplate from 'pages/Shipments/PrintOrders/MainTemplate';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';
import { languages } from 'utils/localization';

import { faChevronUp } from '@fortawesome/pro-regular-svg-icons';

import * as S from '../../Print.styled';
import usePrintUtils, { defaultOrderData, fontSizes, fontWeights } from '../../hooks/usePrintUtils';
import ConfigurationOptionEnumList from '../common/ConfigurationOptionEnumList';
import ConfigurationOptionList from '../common/ConfigurationOptionList';
import OrderAutocomplete from '../common/OrderAutocomplete';

const configOptions = [
	{
		type: 'enum',
		name: 'pageSize',
		data: ['A4', 'A5', 'A6', 'A7', '100x100', '100x120'],
		toolTip: 'print.templatePageSize'
	},
	{
		type: 'enum',
		name: 'fontSize',
		data: Object.keys(fontSizes),
		toolTip: 'print.templateFontSize'
	},
	{
		type: 'enum',
		name: 'fontWeight',
		data: Object.keys(fontWeights),
		toolTip: 'print.templateFontWeight'
	},
	{
		type: 'enum',
		name: 'language',
		data: languages.map((lang) => lang.code),
		toolTip: 'print.templateLanguage'
	},
	{
		type: 'boolean',
		name: 'showShipmentNumber'
	},
	{
		type: 'boolean',
		name: 'showSerialNumber'
	},
	{
		type: 'boolean',
		name: 'showItemQuantity'
	},
	{
		type: 'boolean',
		name: 'showDeliveryCompanyLogo'
	},
	{
		type: 'boolean',
		name: 'showOrderNumber'
	},
	{
		type: 'boolean',
		name: 'showOrderDate'
	},
	{
		type: 'boolean',
		name: 'showShipmentDate'
	},
	{
		type: 'boolean',
		name: 'showCity'
	},
	{
		type: 'boolean',
		name: 'showRegion'
	},
	{
		type: 'boolean',
		name: 'showDistrict'
	},
	{
		type: 'boolean',
		name: 'showArea'
	},
	{
		type: 'boolean',
		name: 'showPaymentMethod'
	},
	{
		type: 'boolean',
		name: 'showTotalDue'
	},
	{
		type: 'boolean',
		name: 'showDriverName'
	},
	{
		type: 'boolean',
		name: 'showOrderComment'
	},
	{
		type: 'boolean',
		name: 'showSenderAddress'
	},
	{
		type: 'boolean',
		name: 'showAddress'
	},
	{
		type: 'boolean',
		name: 'showShippingDescription'
	},
	{
		type: 'boolean',
		name: 'showGrandTotal'
	},
	{
		type: 'boolean',
		name: 'showDescription'
	},
	{
		type: 'boolean',
		name: 'showOrderNumberBarcode'
	},
	{
		type: 'boolean',
		name: 'showOrderItems'
	},
	{
		type: 'boolean',
		name: 'showFooterOnTop'
	},
	{
		type: 'boolean',
		name: 'showRef1'
	},
	{
		type: 'boolean',
		name: 'showSenderPhoneNumber'
	},
	{
		type: 'boolean',
		name: 'showReceiverPhoneNumber'
	},
	{
		type: 'boolean',
		name: 'showOrderPlatform'
	},
	{
		type: 'boolean',
		name: 'hidePageNumber'
	},
	{
		type: 'boolean',
		name: 'showSortingCode'
	}
];

const AWBTemplateForm = ({ formMethods, fieldName, propJSON, isLanguageFetched }) => {
	// States

	// Hooks
	const isAuthorized = useAccessControl();

	const { orderData, handlePDFDownload, pdfFile, isLoading, setOrderData, handleSingleOrderChange, mergePDF } =
		usePrintUtils({
			propJSON
		});

	const companySettings = useSelector((state) => state?.user?.generalSettings) ?? null;
	const { mobile = false } = useSelector((state) => state?.layout);
	const { t } = useTranslation();
	const { Country } = useLocale();

	const isMobileDevice = useMediaQuery(Breakpoints.mobile);

	const loading = useMemo(() => isLoading || !!pdfFile === false, [isLoading, pdfFile]);

	// Helpers
	const createPDF = async (orderPrintData, propJSON) => {
		await mergePDF(async (merger) => {
			if (orderPrintData) {
				// split itemsList array into 4 element arrays and write each array into a page
				const itemList = orderPrintData?.items ? [...orderPrintData.items] : [];
				const splitItemList = [];

				if (propJSON?.showOrderItems) {
					if (propJSON?.numberOfShownItems && propJSON?.numberOfShownItems > 0) {
						const { numberOfShownItems } = propJSON;
						splitItemList.push(itemList.splice(0, numberOfShownItems));
					} else {
						while (itemList.length > 0) {
							splitItemList.push(itemList.splice(0, 4));
						}
					}
				}

				// multiple box order
				if (orderPrintData?.boxes?.length > 0 || orderPrintData?.packageCount > 1) {
					const { boxes, packageCount = 1 } = orderPrintData;

					const boxCount = Math.max(boxes?.length ?? 0, packageCount ?? 0);

					for (let i = 0; i < boxCount; i += 1) {
						if (!splitItemList.length) {
							const pdfBlob = await pdf(
								React.createElement(
									MainTemplate,
									{
										propJSON,
										boxIndex: i,
										boxCount,
										companyLogo: companySettings?.CompanyLogo ?? null,
										countryISO: Country,
										isAuthorizedOMS: isAuthorized('OMSModule'),
										fieldName: 'awbPrintPropJSON',
										...orderPrintData,
										items: [],
										allItemsLength: orderPrintData?.items?.length ?? 0
									},
									{
										key: `${orderPrintData?.orderID}-${i}`
									}
								)
							).toBlob();
							await merger.add(pdfBlob);
						} else {
							for await (const [splitIndex, items] of splitItemList.entries()) {
								const pdfBlob = await pdf(
									React.createElement(
										MainTemplate,
										{
											propJSON,
											boxIndex: i,
											boxCount,
											companyLogo: companySettings?.CompanyLogo ?? null,
											countryISO: Country,
											isAuthorizedOMS: isAuthorized('OMSModule'),
											fieldName: 'awbPrintPropJSON',
											...orderPrintData,
											items,
											allItemsLength: orderPrintData?.items?.length ?? 0
										},
										{
											key: `${orderPrintData?.orderID}-${i}-${splitIndex}`
										}
									)
								).toBlob();
								await merger.add(pdfBlob);
							}
						}
					}
				} else {
					if (!splitItemList.length) {
						const pdfBlob = await pdf(
							React.createElement(
								MainTemplate,
								{
									propJSON,
									boxIndex: 0,
									boxCount: 1,
									companyLogo: companySettings?.CompanyLogo ?? null,
									countryISO: Country,
									isAuthorizedOMS: isAuthorized('OMSModule'),
									fieldName: 'awbPrintPropJSON',
									...orderPrintData,
									items: [],
									allItemsLength: orderPrintData?.items?.length ?? 0
								},
								{ key: `${orderPrintData?.orderID}-1` }
							)
						).toBlob();
						await merger.add(pdfBlob);
					} else {
						for await (const [splitIndex, items] of splitItemList.entries()) {
							const pdfBlob = await pdf(
								React.createElement(
									MainTemplate,
									{
										propJSON,
										boxIndex: 0,
										boxCount: 1,
										companyLogo: companySettings?.CompanyLogo ?? null,
										countryISO: Country,
										fieldName: 'awbPrintPropJSON',
										...orderPrintData,
										items,
										allItemsLength: orderPrintData?.items?.length ?? 0
									},
									{
										key: `${orderPrintData?.orderID}-1-${splitIndex}`
									}
								)
							).toBlob();
							await merger.add(pdfBlob);
						}
					}
				}
			} else {
				AppLogger.error('PrintAWB : No file path or error code!');
			}

			const mergedPdf = await merger.saveAsBlob();
			if (mergedPdf) {
				return mergedPdf;
			}
		});
	};

	// Effects
	useEffect(() => {
		const conditions = [
			!!propJSON,
			!!companySettings,
			orderData !== null && orderData !== undefined,
			isLanguageFetched === true
		];

		const result = conditions.every((condition) => condition);
		if (!result) return;

		const timeout = setTimeout(() => createPDF(orderData, propJSON), 500);

		return () => clearTimeout(timeout);
	}, [JSON.stringify(propJSON), companySettings, orderData, isLanguageFetched]);

	const [isAdvancedSettingsOpen, setIsAdvancedSettingsOpen] = useState(false);

	return (
		<Form
			formMethods={formMethods}
			borderless
			style={{
				padding: '0.25rem'
			}}>
			<S.TemplateContainer>
				<>
					<S.PdfContainer>
						{!loading ? (
							<>
								{mobile && pdfFile && (
									<FormItem
										colSpan={{
											md: 2
										}}>
										<Button
											fullWidth={true}
											onClick={() => handlePDFDownload(pdfFile, `AWB-Template`)}>
											{t('download')}
										</Button>
									</FormItem>
								)}
								{!isMobileDevice && (
									<div
										style={{
											height: '80dvh',
											width: '100%'
										}}
										dangerouslySetInnerHTML={{
											__html: `<iframe src='${pdfFile}' width='100%' height='100%' />`
										}}
									/>
								)}
							</>
						) : (
							<Loading
								style={{
									height: '80dvh',
									width: '100%'
								}}
							/>
						)}
					</S.PdfContainer>
				</>

				<S.ConfigContainer>
					<S.ConfigEnums>
						<OrderAutocomplete
							label="orderNo"
							toolTip="print.selectOrder"
							isLoading={loading}
							handleChange={handleSingleOrderChange}
							generateOrderData={setOrderData}
							defaultOrderData={defaultOrderData}
						/>

						<ConfigurationOptionEnumList
							configOptions={configOptions}
							fieldName={fieldName}
							formMethods={formMethods}
							isLoading={loading}
							propJSON={propJSON}
						/>
					</S.ConfigEnums>
					<S.ConfigOptions>
						<ConfigurationOptionList
							configOptions={configOptions}
							fieldName={fieldName}
							isLoading={loading}
						/>
					</S.ConfigOptions>
					<div>
						<S.AdvancedSettingsButton
							variant="borderless"
							size="small"
							fullWidth
							onClick={() => setIsAdvancedSettingsOpen(!isAdvancedSettingsOpen)}>
							<Typography fontSize="1.7rem" fontWeight={700}>
								advancedSettings
							</Typography>
							<Icon icon={faChevronUp} rotation={isAdvancedSettingsOpen ? 0 : 180} />
						</S.AdvancedSettingsButton>
						{isAdvancedSettingsOpen && (
							<>
								<FormItem
									label="pageMarginHorizontal"
									toolTip="pageMarginHorizontalTooltip"
									isLoading={loading}>
									<IntegerInput
										placeholder="pageMarginHorizontalPlaceholder"
										name={`${fieldName}.pageMarginHorizontal`}
									/>
								</FormItem>
								<FormItem
									label="pageMarginVertical"
									toolTip="pageMarginVerticalTooltip"
									isLoading={loading}>
									<IntegerInput
										placeholder="pageMarginVerticalPlaceholder"
										name={`${fieldName}.pageMarginVertical`}
									/>
								</FormItem>

								<FormItem
									label="shipmentNumberBarcodeFullWidth"
									toolTip="shipmentNumberBarcodeFullWidthTooltip"
									isLoading={loading}>
									<SwitchInput name={`${fieldName}.shipmentNumberBarcodeFullWidth`} />
								</FormItem>
								{!formMethods.watch(fieldName).shipmentNumberBarcodeFullWidth && (
									<>
										<FormItem
											label="shipmentNumberBarcodeWidth"
											toolTip="shipmentNumberBarcodeWidthTooltip"
											isLoading={loading}>
											<IntegerInput
												name={`${fieldName}.shipmentNumberBarcodeWidth`}
												placeholder={'shipmentNumberBarcodeWidthPlaceholder'}
												allowFloat
												decimalLimit={2}
											/>
										</FormItem>
										<FormItem
											label="shipmentNumberBarcodeHeight"
											toolTip="shipmentNumberBarcodeHeightTooltip"
											isLoading={loading}>
											<IntegerInput
												name={`${fieldName}.shipmentNumberBarcodeHeight`}
												placeholder={'shipmentNumberBarcodeHeightPlaceholder'}
												allowFloat
												decimalLimit={2}
											/>
										</FormItem>
									</>
								)}

								<FormItem
									label="orderNumberBarcodeFullWidth"
									toolTip="orderNumberBarcodeFullWidthTooltip"
									isLoading={loading}>
									<SwitchInput name={`${fieldName}.orderNumberBarcodeFullWidth`} />
								</FormItem>
								{!formMethods.watch(fieldName).orderNumberBarcodeFullWidth && (
									<>
										<FormItem
											label="orderNumberBarcodeWidth"
											toolTip="orderNumberBarcodeWidthTooltip"
											isLoading={loading}>
											<IntegerInput
												name={`${fieldName}.orderNumberBarcodeWidth`}
												placeholder={'orderNumberBarcodeWidthPlaceholder'}
												allowFloat
												decimalLimit={2}
											/>
										</FormItem>
										<FormItem
											label="orderNumberBarcodeHeight"
											toolTip="orderNumberBarcodeHeightTooltip"
											isLoading={loading}>
											<IntegerInput
												name={`${fieldName}.orderNumberBarcodeHeight`}
												placeholder={'orderNumberBarcodeHeightPlaceholder'}
												allowFloat
												decimalLimit={2}
											/>
										</FormItem>
									</>
								)}
								<FormItem label="showOtoLogo" isLoading={loading}>
									<SwitchInput
										value={
											propJSON.showOtoLogo === undefined && Country === 'TR'
												? true
												: propJSON.showOtoLogo
										}
										setValue={(e) =>
											formMethods.setValue(`${fieldName}.showOtoLogo`, e.target.checked, {
												shouldValidate: true,
												shouldDirty: true
											})
										}
									/>
								</FormItem>
								<FormItem
									label="showSuccessMessage"
									toolTip="successMessageTooltip"
									isLoading={loading}>
									<SwitchInput name={`${fieldName}.showSuccessMessage`} />
								</FormItem>
								<Authorization path="/awbSettings/hidePoweredByOto">
									<FormItem
										label="hidePoweredByOto"
										toolTip="hidePoweredByOtoTooltip"
										isLoading={loading}>
										<SwitchInput name={`${fieldName}.hidePoweredByOto`} />
									</FormItem>
								</Authorization>
								<FormItem
									label="numberOfCharactersPerItem"
									toolTip="numberOfCharactersPerItemTooltip"
									isLoading={loading}>
									<IntegerInput
										name={`${fieldName}.numberOfCharactersPerItem`}
										placeholder={'numberOfCharactersPerItemPlaceholder'}
										min={1}
									/>
								</FormItem>
								<FormItem
									label="numberOfShownItems"
									toolTip="numberOfShownItemsTooltip"
									isLoading={loading}>
									<IntegerInput
										name={`${fieldName}.numberOfShownItems`}
										placeholder={'numberOfShownItemsPlaceholder'}
										min={1}
										max={4}
									/>
								</FormItem>
							</>
						)}
					</div>
				</S.ConfigContainer>
			</S.TemplateContainer>
		</Form>
	);
};

export default AWBTemplateForm;
